import { Injectable } from '@angular/core';
import * as localforage from 'localforage';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import {
  IDeleteParams,
  IListParams,
  IPaginationState,
  IScrollPosition,
  IStoreProps,
} from 'src/app/talent-litmus-core/common/common.interface';
import {
  addEntities,
  deleteEntities,
  getEntity,
  selectAllEntities,
  setEntities,
  updateEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { ISequence } from './sequence.interface';
import { withPagination } from '@ngneat/elf-pagination';
import { persistState } from '@ngneat/elf-persist-state';
import * as moment from 'moment';
import { REFRESH_TIME_IN_MINUTES } from 'src/app/shared/configs/global-values.constants';
import { ApiBaseService } from 'src/app/shared/services/api/api-base/api-base.service';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { PageEvent } from '@angular/material/paginator';

// eslint-disable-next-line @typescript-eslint/naming-convention
const STORE_NAME = 'lvl2-sequence';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'talent-litmus-adminv2',
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },
  withProps<IStoreProps>({ lastRefreshed: undefined }),
  withProps<IPaginationState>({
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0,
  }),
  withEntities<ISequence, '_id'>({ idKey: '_id' }),
  withProps<IScrollPosition>({ x: 0, y: 0 }),
  withProps<{ selectedId: string }>({ selectedId: null }),
  withProps<{ searchParam: string }>({ searchParam: '' }),
  withPagination(),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class SequenceCoreService {
  private _loading = false;

  /**
   * @name sequenceList$
   * @description emits stored list of the sequences
   */

  sequenceList$ = store.pipe(selectAllEntities());
  paginationState$ = store.pipe(
    select((state) => {
      return state;
    }),
  );
  totalCount: number;

  public get loading(): boolean {
    return this._loading;
  }

  constructor(private _api: ApiBaseService) {
    if (!store.value.lastRefreshed) {
      setTimeout(() => {
        this.refresh();
      }, 300);
    } else {
      this.refresh();
    }
  }

  moveToLastPage(): void {
    let length: number;
    let pageSize: number;
    let previousPageIndex: number;
    this.paginationState$.subscribe((state) => {
      length = state.length;
      pageSize = state.pageSize;
      previousPageIndex = state.previousPageIndex;
    });
    this.pageChangeEvent({
      length,
      pageIndex: Math.ceil(length / pageSize),
      pageSize,
      previousPageIndex,
    });
  }

  pageChangeEvent(event: PageEvent): void {
    store.update((state) => ({
      ...state,
      length: event.length,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex,
    }));
  }

  setScrollPosition(scrollPostion: [number, number]): void {
    store.update((state) => ({
      ...state,
      x: scrollPostion[0],
      y: scrollPostion[1],
    }));
  }

  setSelectedId(id: string): void {
    store.update((state) => ({
      ...state,
      selectedId: id,
    }));
  }

  setSearchParam(searchParam: string): void {
    store.update((state) => ({
      ...state,
      searchParam: searchParam,
    }));
  }

  getSequences(params?: IListParams): void {
    this._loading = true;
    this._api
      .get('sequences/list', params || {})
      .subscribe((data: ISequenceListResponse): void => {
        this.totalCount = data.totalCount;
        store.update(
          setEntities(data.list),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      });
  }

  public addSequence(dataInput): Observable<any> {
    this._loading = true;
    return this._api.post('sequences/add', dataInput, {}).pipe(
      tap((data: ISequence) => {
        store.update(addEntities(data), setProp('lastRefreshed', new Date()));
      }),
    );
  }

  public translateSequence(dataInput): Observable<any> {
    this._loading = true;
    return this._api.put('sequences/translate', dataInput, {}).pipe(
      tap(() => {
        store.update(
          updateEntities(dataInput['_id'], dataInput),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      }),
    );
  }

  public getSequenceInfo(id: string): Observable<ISequence> {
    return this._api.get(`sequences/${id}`);
  }

  public getSequence(id: string): ISequence {
    const sequence = store.query(getEntity(id));
    return sequence;
  }

  public updateSequence(dataInput): Observable<any> {
    this._loading = true;
    return this._api.put('sequences/edit', dataInput, {}).pipe(
      tap(() => {
        store.update(
          updateEntities(dataInput['_id'], dataInput),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      }),
    );
  }

  public deleteSequence(dataInput: IDeleteParams): Observable<any> {
    this._loading = true;
    return this._api.delete('sequences', { body: dataInput }).pipe(
      tap(() => {
        store.update(deleteEntities(dataInput['_id']));
        this._loading = false;
      }),
    );
  }

  public resolveSequenceId(sequence: string | ISequence): string {
    let sequenceId: string | ISequence = '';
    if (sequence) {
      sequenceId = sequence;
    }
    if (sequence['_id']) {
      sequenceId = sequence['_id'];
    }
    return sequenceId as string;
  }

  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') >
        REFRESH_TIME_IN_MINUTES
    ) {
      this.getSequences();
    }
  }
}

interface ISequenceListResponse {
  list: ISequence[];
  totalCount: number;
}
